import {
    Alignment, ApiMetadata, ArrayUtil, Collection, Color, DOMUtil, DynamicLoader, GeneralSettings, getTheme,
    getThemeColor, getThemeFontSize, getThemeForKey, HorizontalAlignment, JSUtil, Keys, LocalUserSettings,
    MetadataField, ModelRow, ScrollOptions, Size, StringUtil
} from "@mcleod/core";
import {
    BlurListener, Button, ComponentBehavior, ContextMenu, ContextMenuCreationEvent, ContextMenuCreationListener,
    DataSource, DataSourceMode, DesignerStyles, FocusListener, Label, Layout, PanelProps, TableRow, TooltipOptions
} from "..";
import { CompanyType, LtlType } from "../Company";
import { LabelStyles } from "../components/label/LabelStyles";
import { ClickEvent, ClickListener } from "../events/ClickEvent";
import { DataDisplayEvent, DataDisplayListener } from "../events/DataDisplayEvent";
import { DomEvent } from "../events/DomEvent";
import { DragListener } from "../events/DragEvent";
import { Event } from "../events/Event";
import { KeyListener } from "../events/KeyEvent";
import { KeyHandler } from "../events/KeyHandler";
import { ModifierKeys } from "../events/KeyModifiers";
import { MouseEvent, MouseListener } from "../events/MouseEvent";
import { ResizeEvent, ResizeListener } from "../events/ResizeEvent";
import { ComponentCreator } from "../page/ComponentFactory";
import { ComponentStyles } from "./ComponentStyles";
import { Overlay } from "../page/Overlay";
import { OverlayProps } from "../page/OverlayProps";
import { DeserializeProps } from "../serializer/ComponentDeserializer";
import { BorderType } from "./BorderType";
import { ComponentListenerDefs } from "./ComponentListenerDefs";
import { ComponentPropDefinition, ComponentProps, IdProps, TooltipCallback } from "./ComponentProps";
import { Container } from "./Container";
import { Cursor } from "./Cursor";
import { DesignableObject } from "./DesignableObject";
import { DesignerInterface } from "./DesignerInterface";
import { ListenerListDef } from "./ListenerListDef";
import { MainPageAccessor } from "./MainPageAccessor";
import { MountUtil } from "./MountListener";
import { PermDefinitionConstants, PermissionsDefinition } from "./PermissionsDefinition";
import { TransitionOptions } from "./TransitionOptions";
import { TransitionsUtil } from "./TransitionsUtil";
import { ValidationResult } from "./ValidationResult";
import { DesignableObjectLogManager } from "../logging/DesignableObjectLogManager";
import { ContextMenuItemsProvider } from "../components/contextmenu/ContextMenuLabelProps";
import { ComponentUtil } from "./ComponentUtil";
import { ComponentTooltipHandler } from "./ComponentTooltipHandler";
import { AuditLogView } from "../../../common/src/AuditLogView";
import { BehaviorTypes } from "./BehaviorTypes";

const _dataDisplayListenerDef: ListenerListDef = { listName: "_dataDisplayListeners" };
const _contextMenuCreationListenerDef: ListenerListDef = { listName: "_contextMenuCreation" };

const log = DesignableObjectLogManager.getLogger("components/Component");

const DOUBLE_CLICK_TIME = 200;

export interface RippleOptions {
    event: Event | DomEvent;
    speed: number;
}

export enum ContextMenuClickType {
    RIGHT, LEFT
}

export type ComponentTypeTestValue = string|(new (...args: any[]) => Component);

/**
 * Component is the base class for all UI elements.  It defines the properties, methods, events, and other behavior that is common
 * to every subclass.
 *
 * Components have a root _element that is HTMLElement.  This is what is added to the DOM when the Component is added to a Container.
 *
 * It has several methods that are meant to be overridden by subclasses.
 */
export class Component extends DesignableObject implements ComponentProps {
    private static _componentBehavior: ComponentBehavior;
    protected __designer: DesignerInterface;
    public _element: HTMLElement; // make it public, but still give it private naming so folks know they shouldn't use it
    private _parent: Container;
    protected _boundField: MetadataField;
    protected _align: HorizontalAlignment;
    private _keyHandlers: KeyHandler[];
    private _backgroundColor: Color;
    private _borderRadius: string | number;
    private _borderBottomLeftRadius: string | number;
    private _borderBottomRightRadius: string | number;
    private _borderTopLeftRadius: string | number;
    private _borderTopRightRadius: string | number;
    private _borderColor: Color;
    private _borderType: BorderType;
    private _borderShadow: boolean;
    private _borderWidth: number;
    private _borderBottomColor: Color;
    private _borderBottomType: BorderType;
    private _borderBottomWidth: number;
    private _borderLeftColor: Color;
    private _borderLeftType: BorderType;
    private _borderLeftWidth: number;
    private _borderRightColor: Color;
    private _borderRightType: BorderType;
    private _borderRightWidth: number;
    private _borderTopColor: Color;
    private _borderTopType: BorderType;
    private _borderTopWidth: number;
    private _bottom: string | number;
    private _boundRow: any; //should be a ModelRow, except the PropertiesTable still deals in plain old objects
    private _color: Color;
    private _companyType: CompanyType;
    private _ltlType: LtlType;
    private _cursor: Cursor;
    private _dataSource: DataSource;
    protected _displayLabel: string;
    protected _draggable: boolean;
    private _enabled: boolean;
    private _enabledDuringAdd: boolean;
    private _enabledDuringUpdate: boolean;
    private _enabledDuringSearch: boolean;
    private _deletableInDesigner: boolean|(() => boolean);
    private _draggableInDesigner: boolean|(() => boolean);
    private _deserialized: boolean;
    private _disabledByServer: boolean;
    private _enlarged: boolean;
    private _enlargeScope: Component;
    protected _fillRow: boolean;
    protected _fontBold: boolean;
    private _fontFamily: string;
    private _fontSize: string | number;
    private _fillHeight: boolean;
    private _field: string;
    private _height: string | number;
    private _isCustom: boolean;
    private _isRow: boolean;
    private _left: string | number;
    private _license: string;
    private _managingComponent: Component;
    private _margin: number | string;
    private _marginTop: number | string;
    private _marginBottom: number | string;
    private _marginLeft: number | string;
    private _marginRight: number | string;
    private _minHeight: number | string;
    private _maxHeight: number | string;
    private _minWidth: number | string;
    private _maxWidth: number | string;
    private _padding: number;
    private _paddingTop: number;
    private _paddingBottom: number;
    private _paddingLeft: number;
    private _paddingRight: number;
    private _preFocusValue: any;
    private _rememberUserChoice: boolean;
    private _right: string | number;
    private _required: boolean;
    private _requiredDuringAdd: boolean;
    private _requiredDuringUpdate: boolean;
    private _requiredDuringSearch: boolean;
    private resizeObserver: ResizeObserver;
    _rowBreak: boolean;
    private _searchOnly: boolean;
    private _slideOutRemovalOptions: TransitionOptions;
    private _slideOutOverlay: Overlay;
    private _sortField: string;
    private _excludeFromSortFields: string[];
    private _excludeFromAutogenLayout: boolean;
    private _top: string | number;
    private _visible: boolean;
    private _visibleDuringAdd: boolean;
    private _visibleDuringUpdate: boolean;
    private _visibleDuringSearch: boolean;
    private _preventCollapse: boolean;
    private _width: number | string;
    private _widthFillWeight: number;
    private _zIndex: number;
    private _setPreFocusValueRef = (event: Event) => this._setPreFocusValue(event);
    private dblClickReceived: boolean;
    private _sortDescendingByDefault: boolean;
    private _sortNullsAtEnd: boolean;
    private _nextFocusable: string;
    private _tabKeyHandler: KeyHandler;
    private _hiddenUnlessMouseOver: Component;
    private _hiddenChildComponentMouseEnterListener: MouseListener;
    private _hiddenChildComponentMouseLeaveListener: MouseListener;
    private _doOnDataSourceChanged: (dataSource: DataSource, changesPresent: boolean) => void;
    private _removedByServer: boolean;
    protected supportsEditSecurity: boolean;
    private metaDataPromise: Promise<void> = Promise.resolve();
    private _contextMenuItems: ContextMenuItemsProvider;
    private _contextMenuClickType: ContextMenuClickType = ContextMenuClickType.RIGHT;
    private contextMenuListener = (event) => this.showContextMenu(event);
    private tooltipHandler: ComponentTooltipHandler;
    private _disabledTooltip: ComponentCreator;
    private _tooltipCallback: TooltipCallback;
    protected _tooltip: ComponentCreator;
    private _suppressTooltip: boolean;
    private _appProps: any


    constructor(type: string, props?: IdProps) {
        super(props?.["_creationMethod"]);
        this._element = document.createElement(type);
        this._element["_mcleodComponent"] = this;
        this._initElement(props);
        this.tooltipHandler = new ComponentTooltipHandler(this);
    }

    protected _initElement(props: IdProps) {
        this.addClass(ComponentStyles.base);
        if (GeneralSettings.isRunningAutomation) {
            this.addClass(ComponentStyles.unanimated);
        }
        if (props != null && props.id != null)
            this.id = props.id;
    }

    get id(): string {
        return this._element.id;
    }

    set id(value: string) {
        if (this.__designer?.canModifyProp == null || this.__designer.canModifyProp("id", this)) {
            this._element.id = value;
            this.syncInnerComponentIds();
        }
    }

    public get deserialized(): boolean {
        return this._deserialized;
    }

    public set deserialized(value: boolean) {
        this._deserialized = value;
        if (this._designer != null && this._designer.addDragAndDropListeners != null)
            this._designer.addDragAndDropListeners(this);
    }

    public get isCustom(): boolean {
        return this._isCustom;
    }

    public set isCustom(value: boolean) {
        this._isCustom = value;
        if (value === true && this._designer != null)
            this.setClassIncluded(DesignerStyles.customComponent);
    }

    public get defaultDataValue(): any {
        return null;
    }

    get fillRow(): boolean {
        return this._fillRow;
    }

    set fillRow(value: boolean) {
        this._fillRow = value;
        this._calcFill();
    }

    get widthFillWeight(): number {
        return this._widthFillWeight;
    }

    set widthFillWeight(value: number) {
        this._widthFillWeight = value;
        this._calcFill();
    }

    protected _calcFill() {
        if (this.widthFillWeight != null) {
            this._element.style.flex = this.widthFillWeight.toString();
            if (this._maxWidth == undefined)
                this._element.style.maxWidth = "100%";
        } else if (this.fillRow === true) {
            this.setClassIncluded(ComponentStyles.fill);
        } else {
            this.removeClass(ComponentStyles.fill);
        }
    }

    private setElementHeight() {
        if (this.height == null && (this.fillHeight == null || this.fillHeight === false))
            this._element.style.height = "";
        else if (this.height == null && this.fillHeight === true)
            this._element.style.height = "100%"; // setting flex:1 if the parent container is flex and the flex-direction is row would be better
        else if (this.height != null)
            this._element.style.height = DOMUtil.getSizeSpecifier(this.height);
    }

    get fillHeight(): boolean {
        return this._fillHeight;
    }

    set fillHeight(value: boolean) {
        this._fillHeight = value;
        this.setElementHeight();
        if (value && this._element.parentElement?.className === "pnl-panelRow")
            this._element.parentElement.className += " pnl-rowFillHeight";
        else if (!value && this._element.parentElement?.classList.contains("pnl-rowFillHeight"))
            this._element.parentElement.classList.remove("pnl-rowFillHeight");
    }

    public scrollIntoView(options?: Partial<ScrollOptions>, startingElement?: HTMLElement) {
        const seedElement = startingElement == null ? this._element : startingElement;
        const parent = DOMUtil.findScrollableParent(seedElement);
        DOMUtil.scrollElementIntoView(this._element, options, parent);
    }

    public setClassIncluded(className: string, included: boolean = true) {
        this.internalSetClassIncluded(className, included);
    }

    protected internalSetClassIncluded(className: string, included: boolean = true, element: HTMLElement = this._element) {
        element?.classList.toggle(className, included);
    }

    /**
     * Call this method when you want a simple true/false answer about whether a Component passes its validation.
     *
     * @param checkRequired Whether blank required fields should be considered a validation error
     * @returns
     */
    validateSimple(checkRequired: boolean = true, showErrors: boolean = true): boolean {
        if (this._designer != null)
            return true;
        const results: ValidationResult[] = this.validate(checkRequired, showErrors);
        if (results != null)
            for (const result of results)
                if (!result.isValid)
                    return false;
        return true;
    }

    /**
     * This method is meant to be overriden by subclasses to validate the user's input.
     *
     * @param checkRequired Whether blank required fields should be considered a validation error
     * @param showErrors
     * @returns void
     */

    validate(checkRequired: boolean = true, showErrors: boolean = true): ValidationResult[] {
        return null;
    }

    /**
     * This method is meant to be overriden by subclasses to remove validation warnings.  It will be called when the component's related DataSource changes modes.
     *
     * @returns void
     */

    resetValidation() { }

    /**
     * This will calculate the size of the component if it were allowed to be whatever size it wants to be (height="auto").
     * Note: to calculate the preferred size, we clone the node in an invisible area of the DOM, so this method should be called
     * with that in mind.
     * @returns {object} Object containing width, height
     */
    getPreferredSize(): Size {
        const clone = this._element.cloneNode(true) as HTMLElement;
        clone.style.cssText = "position:fixed; top:-9999px; opacity:0; height: auto";
        document.body.appendChild(clone);
        const result = { width: clone.clientWidth, height: clone.clientHeight };
        clone.parentNode.removeChild(clone);
        return result;
    }

    get className(): string {
        return this._element.className;
    }

    set className(value: string) {
        this._element.className = value;
    }

    addClass(value: string) {
        this.setClassIncluded(value, true);
    }

    removeClass(value: string) {
        this.setClassIncluded(value, false);
    }

    get insideTableCell(): boolean {
        return this.managingComponent?.serializationName === "cell";
    }

    get managingComponent(): Component {
        return this._managingComponent;
    }

    set managingComponent(value: Component) {
        this._managingComponent = value;
    }

    clicked(event?: Event | DomEvent) {
        if (this.removedByServer !== true) {
            this.fireListeners(ComponentListenerDefs.click, new ClickEvent(this, event));
        }
    }

    public addClickListener(value: ClickListener, fireEvenOnDblClick: boolean = false, addAsFirstListener: boolean = false): Component {
        const wrapper = (event: Event) => {
            log.debug(this, "Clicked", this);
            // if this component also has double click listeners, we want to delay firing the click event to make sure
            // that this click isn't the first click of a double click..... I know.  I thought the same thing you're thinking.
            if (!fireEvenOnDblClick && this.hasListeners(ComponentListenerDefs.dblclick)) {
                this.dblClickReceived = false;  // reset the flag that checks for the second click of a double click
                setTimeout(() => {
                    if (this.dblClickReceived !== true)
                        value(event);
                }, DOUBLE_CLICK_TIME);
            } else // if there are no double-click listeners, fire the click listener immediately
                value(event);
        };
        wrapper.wrapped = value; // pure witchcraft

        if (addAsFirstListener) {
            this.insertEventListener(ComponentListenerDefs.click, wrapper, 0);
        } else {
            this.addEventListener(ComponentListenerDefs.click, wrapper);
        }

        if (this.hasListeners(ComponentListenerDefs.click) && this.cursor === undefined)
            this._element.style.cursor = "pointer";
        return this;
    }

    public removeClickListener(value: ClickListener): Component {
        this.removeEventListener(ComponentListenerDefs.click, value) as Component;
        if (!this.hasListeners(ComponentListenerDefs.click) && this.cursor === undefined)
            this._element.style.cursor = null;
        return this;
    }

    public hasClickListeners(): boolean {
        return this.hasListeners(ComponentListenerDefs.click);
    }

    public addDblClickListener(value: ClickListener): Component {
        const wrapper = (event: Event) => {
            this.dblClickReceived = true; // this notifies the click listeners to stand down
            value(event);
        };
        wrapper.wrapped = value;
        this.addEventListener(ComponentListenerDefs.dblclick, wrapper);
        if (this.hasListeners(ComponentListenerDefs.dblclick) && this.cursor === undefined)
            this._element.style.cursor = "pointer";
        return this;
    }

    public removeDblClickListener(value: ClickListener): Component {
        this.removeEventListener(ComponentListenerDefs.dblclick, value) as Component;
        if (this.hasListeners(ComponentListenerDefs.dblclick) && this.cursor === undefined)
            this._element.style.cursor = null;
        return this;
    }

    public hasDblClickListeners(): boolean {
        return this.hasListeners(ComponentListenerDefs.dblclick);
    }

    public addMouseDownListener(value: MouseListener): Component {
        return this.addEventListener(ComponentListenerDefs.mouseDown, value) as Component;
    }

    public removeMouseDownListener(value: MouseListener): Component {
        return this.removeEventListener(ComponentListenerDefs.mouseDown, value) as Component;
    }

    public addMouseUpListener(value: MouseListener): Component {
        return this.addEventListener(ComponentListenerDefs.mouseUp, value) as Component;
    }

    public removeMouseUpListener(value: MouseListener): Component {
        return this.removeEventListener(ComponentListenerDefs.mouseUp, value) as Component;
    }

    public addMouseEnterListener(value: MouseListener): Component {
        return this.addEventListener(ComponentListenerDefs.mouseEnter, value) as Component;
    }

    public removeMouseEnterListener(value: MouseListener): Component {
        return this.removeEventListener(ComponentListenerDefs.mouseEnter, value) as Component;
    }

    public addMouseLeaveListener(value: MouseListener): Component {
        return this.addEventListener(ComponentListenerDefs.mouseLeave, value) as Component;
    }

    public removeMouseLeaveListener(value: MouseListener): Component {
        return this.removeEventListener(ComponentListenerDefs.mouseLeave, value) as Component;
    }

    public addMouseMoveListener(value: MouseListener): Component {
        return this.addEventListener(ComponentListenerDefs.mouseMove, value) as Component;
    }

    public removeMouseMoveListener(value: MouseListener): Component {
        return this.removeEventListener(ComponentListenerDefs.mouseMove, value) as Component;
    }

    public addKeyDownListener(value: KeyListener): Component {
        return this.addEventListener(ComponentListenerDefs.keyDown, value) as Component;
    }

    public removeKeyDownListener(value: KeyListener): Component {
        return this.removeEventListener(ComponentListenerDefs.keyDown, value) as Component;
    }

    public addKeyUpListener(value: KeyListener): Component {
        return this.addEventListener(ComponentListenerDefs.keyUp, value) as Component;
    }

    public removeKeyUpListener(value: KeyListener): Component {
        return this.removeEventListener(ComponentListenerDefs.keyUp, value) as Component;
    }

    public addDragStartListener(value: DragListener): Component {
        return this.addEventListener(ComponentListenerDefs.dragStart, value) as Component;
    }

    public removeDragStartListener(value: DragListener): Component {
        return this.removeEventListener(ComponentListenerDefs.dragStart, value) as Component;
    }

    public addDragEndListener(value: DragListener): Component {
        return this.addEventListener(ComponentListenerDefs.dragEnd, value) as Component;
    }

    public removeDragEndListener(value: DragListener): Component {
        return this.removeEventListener(ComponentListenerDefs.dragEnd, value) as Component;
    }

    public addDragEnterListener(value: DragListener): Component {
        return this.addEventListener(ComponentListenerDefs.dragEnter, value) as Component;
    }

    public removeDragEnterListener(value: DragListener): Component {
        return this.removeEventListener(ComponentListenerDefs.dragEnter, value) as Component;
    }

    public addDragLeaveListener(value: DragListener): Component {
        return this.addEventListener(ComponentListenerDefs.dragLeave, value) as Component;
    }

    public removeDragLeaveListener(value: DragListener): Component {
        return this.removeEventListener(ComponentListenerDefs.dragLeave, value) as Component;
    }

    public addDragOverListener(value: DragListener): Component {
        return this.addEventListener(ComponentListenerDefs.dragOver, value) as Component;
    }

    public removeDragOverListener(value: DragListener): Component {
        return this.removeEventListener(ComponentListenerDefs.dragOver, value) as Component;
    }

    public addDropListener(value: DragListener): Component {
        return this.addEventListener(ComponentListenerDefs.dragDrop, value) as Component;
    }

    public removeDropListener(value: DragListener): Component {
        return this.removeEventListener(ComponentListenerDefs.dragDrop, value) as Component;
    }

    public addFocusListener(value: FocusListener): Component {
        return this.addEventListener(ComponentListenerDefs.focus, value) as Component;
    }

    public insertFocusListener(value: FocusListener, index: number): Component {
        return this.insertEventListener(ComponentListenerDefs.focus, value, index) as Component;
    }

    public removeFocusListener(value: FocusListener): Component {
        return this.removeEventListener(ComponentListenerDefs.focus, value) as Component;
    }

    public addBlurListener(value: BlurListener): Component {
        this.addEventListener(ComponentListenerDefs.focus, this._setPreFocusValueRef);
        return this.addEventListener(ComponentListenerDefs.blur, value) as Component;
    }

    public removeBlurListener(value: BlurListener): Component {
        return this.removeEventListener(ComponentListenerDefs.blur, value) as Component;
    }

    public addDataDisplayListener(value: DataDisplayListener): Component {
        return this.addEventListener(_dataDisplayListenerDef, value) as Component;
    }

    public removeDataDisplayListener(value: DataDisplayListener): Component {
        return this.removeEventListener(_dataDisplayListenerDef, value) as Component;
    }

    public fireDataDisplayListeners(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        this.fireListeners(_dataDisplayListenerDef, () => new DataDisplayEvent(this, data, allData, rowIndex));
    }

    public addContextMenuCreationListener(value: ContextMenuCreationListener): Component {
        return this.addEventListener(_contextMenuCreationListenerDef, value) as Component;
    }

    public removeContextMenuCreationListener(value: ContextMenuCreationListener): Component {
        return this.removeEventListener(_contextMenuCreationListenerDef, value) as Component;
    }

    public fireContextMenuCreationListeners(event: ContextMenuCreationEvent) {
        this.fireListeners(_contextMenuCreationListenerDef, event);
    }

    public focus() {
        this.getFocusTarget()?.focus();
    }

    protected getFocusTarget(): HTMLElement {
        return this._element;
    }

    isFocusable() {
        return this.visible && this.getFocusTarget() != null;
    }

    public hasFocus(): boolean {
        return DOMUtil.isActiveElement(this.getFocusTarget());
    }

    public getEventTarget(): HTMLElement {
        return this._element;
    }

    public get style(): CSSStyleDeclaration {
        return this._element.style;
    }

    public set style(value: CSSStyleDeclaration) {
        this.setStyleProps(value);
    }

    public setStyleProps(value: Partial<CSSStyleDeclaration>) {
        if (value != null)
            for (const key in value)
                this._element.style[key] = value[key];
    }

    public get fontBold(): boolean {
        return this._fontBold;
    }

    public set fontBold(value: boolean) {
        this._fontBold = value;
        if (this.getFontTarget() == null) {
            return;
        }
        if (value)
            this.getFontTarget().style.fontWeight = "500";
        else
            this.getFontTarget().style.fontWeight = "";
    }

    getDragTarget(): HTMLElement {
        return this._element;
    }

    /**
     * Meant to be overridden for components that wish font properties to affect something other than the root element.
     * @returns
     */
    protected getFontTarget(): HTMLElement {
        return this._element;
    }

    public get color(): Color {
        return this._color;
    }

    public set color(value: Color) {
        this._color = value;
        this._element.style.color = value == null ? "" : getThemeColor(value);
    }

    public get backgroundColor(): Color {
        return this._backgroundColor;
    }

    public set backgroundColor(value: Color) {
        this._backgroundColor = value;
        this._element.style.backgroundColor = value == null ? "" : getThemeColor(value);
    }

    public get align(): HorizontalAlignment {
        return this._align;
    }

    public set align(value: HorizontalAlignment) {
        this._align = value;
        if (value === HorizontalAlignment.RIGHT) {
            this._element.style.justifyContent = "flex-end";
            this._element.style.textAlign = "right";
        } else if (value === HorizontalAlignment.CENTER) {
            this._element.style.justifyContent = "center";
            this._element.style.textAlign = "center";
        } else {
            this._element.style.justifyContent = "flex-start";
            this._element.style.textAlign = "left";
        }
    }

    public get fontFamily(): string {
        return this._fontFamily;
    }

    public set fontFamily(value: string) {
        this._fontFamily = value;
        this.getFontTarget().style.fontFamily = value; // should make some theming (an enum) for this
    }

    public get fontSize(): string | number {
        return this._fontSize;
    }

    public set fontSize(value: string | number) {
        this._fontSize = value;
        this.getFontTarget().style.fontSize = getThemeFontSize(value);
    }

    public get _designer(): DesignerInterface {
        return this.__designer;
    }

    public set _designer(value: DesignerInterface) {
        this.__designer = value;
        if (value != null) {
            this._element.onclick = (event) => this._clickedInDesigner(new MouseEvent(this, event));
            this._interactionEnabled = false;
        }
    }

    /**
     * This is called when the Component is initially added to the UI Designer.  Components can override
     * this to establish initial state for the component.  Table and Tabset use this to add an initial column and
     * tab, respectively.
     */
    protected _initialDropInDesigner() {
    }

    private _clickedInDesigner(event: MouseEvent) {
        if (this._designer == null)
            return;
        const nestedContainer = this._getNestedContainer(this);
        if (nestedContainer == null)
            this._designer.selectComponent(this, event.ctrlKey);
        else
            this._designer.selectComponent(nestedContainer, event.ctrlKey);
        event.stopPropagation();
    }

    get deletableInDesigner(): boolean {
        if (this._deletableInDesigner == null)
            return true;
        return (typeof this._deletableInDesigner === "boolean") ? this._deletableInDesigner :
            this._deletableInDesigner();
    }

    set deletableInDesigner(value: boolean|(() => boolean)) {
        this._deletableInDesigner = value;
    }

    get draggableInDesigner(): boolean {
        if (this._draggableInDesigner == null)
            return true;
        return (typeof this._draggableInDesigner === "boolean") ? this._draggableInDesigner :
            this._draggableInDesigner();
    }

    set draggableInDesigner(value: boolean|(() => boolean)) {
        this._draggableInDesigner = value;
    }

    /**
     * Meant to be overridden by any component that has a "default" event type.  This is used to add the code skeleton for the event handler when the user double clicks the component in the designer.
     *
     * @returns The name of the event that is considered the default event for this component type.
     */
    protected _getDefaultEventProp(): string {
        return null;
    }

    private _getNestedContainer(comp: Component) {
        // this totally doesn't work and will need to be fixed when we start working with Layouts that are nested
        while (comp != null) {
            const proto = Object.getPrototypeOf(comp);
            if (proto != null && proto.constructor != null && (comp.isNestedDesignerContainer() === true || proto.constructor.name === "UsedToBeNestedLayoutButDon'tMakeItLayout")) // don't add instanceof here so we don't import that module
                return comp;
            comp = comp._parent;
        }
        return null;
    }

    public isNestedDesignerContainer(): boolean {
        return false;
    }

    public get companyType(): CompanyType {
        return this._companyType == null ? this.getPropertyDefinitions().companyType.defaultValue : this._companyType;
    }

    public set companyType(value: CompanyType) {
        this._companyType = value;
    }

    protected get contextMenuClickType(): ContextMenuClickType {
        return this._contextMenuClickType;
    }

    protected set contextMenuClickType(value: ContextMenuClickType) {
        this._contextMenuClickType = value;
        this.syncContextMenuListeners();
    }

    public get contextMenuItems(): ContextMenuItemsProvider {
        return this._contextMenuItems;
    }

    public set contextMenuItems(value: ContextMenuItemsProvider) {
        this._contextMenuItems = value;
        this.syncContextMenuListeners();
        if (this._designer != null) {
            const hasDesignableContextMenu = this._contextMenuItems != null && typeof this._contextMenuItems !== "function";
            this.setClassIncluded(DesignerStyles.hasContextMenu, hasDesignableContextMenu);
        }
    }

    private syncContextMenuListeners() {
        this._element.removeEventListener("contextmenu", this.contextMenuListener);
        this.removeClickListener(this.contextMenuListener);
        if (this.contextMenuClickType === ContextMenuClickType.LEFT) {
            this.addClickListener(this.contextMenuListener);
        }
        else {
            this._element.addEventListener("contextmenu", this.contextMenuListener);
        }
    }

    public get ltlType(): LtlType {
        return this._ltlType;
    }

    public set ltlType(value: LtlType) {
        this._ltlType = value;
    }

    public get cursor(): Cursor {
        return this._cursor;
    }

    public set cursor(value: Cursor) {
        this._cursor = value;
        this._element.style.cursor = value;
    }

    public get draggable(): boolean {
        return this._draggable;
    }

    public set draggable(value: boolean) {
        const target = this.getDragTarget();
        if (target != null) {
            this._draggable = value;
            if (this._designer == null)
                target.draggable = value;
        }
    }

    public get left(): string | number {
        return this._left;
    }

    public set left(value: string | number) {
        this._left = value;
        this._element.style.left = DOMUtil.getSizeSpecifier(value);
    }

    public get top(): string | number {
        return this._top;
    }

    public set top(value: string | number) {
        this._top = value;
        this._element.style.top = DOMUtil.getSizeSpecifier(value);
    }

    public get right(): string | number {
        return this._right;
    }

    public set right(value: string | number) {
        this._right = value;
        this._element.style.right = DOMUtil.getSizeSpecifier(value);
    }

    public get bottom(): string | number {
        return this._bottom;
    }

    public set bottom(value: string | number) {
        this._bottom = value;
        this._element.style.bottom = DOMUtil.getSizeSpecifier(value);
    }

    public get width(): string | number {
        return this._width;
    }

    public set width(value: string | number) {
        this._width = value;
        this._element.style.width = DOMUtil.getSizeSpecifier(value);
    }

    public get minWidth(): string | number {
        return this._minWidth;
    }

    public set minWidth(value: string | number) {
        this._minWidth = value;
        this._element.style.minWidth = DOMUtil.getSizeSpecifier(value);
    }

    public get maxWidth(): string | number {
        return this._maxWidth;
    }

    public set maxWidth(value: string | number) {
        this._maxWidth = value;
        this._element.style.maxWidth = DOMUtil.getSizeSpecifier(value);
    }

    public get height(): string | number {
        return this._height;
    }

    public set height(value: string | number) {
        this._height = value;
        this.setElementHeight();
    }

    public get minHeight(): string | number {
        return this._minHeight;
    }

    public set minHeight(value: string | number) {
        this._minHeight = value;
        this._element.style.minHeight = DOMUtil.getSizeSpecifier(value);
    }

    public get maxHeight(): string | number {
        return this._maxHeight;
    }

    public set maxHeight(value: string | number) {
        this._maxHeight = value;
        this._element.style.maxHeight = DOMUtil.getSizeSpecifier(value);
    }

    public get borderShadow(): boolean {
        return this._borderShadow;
    }

    public set borderShadow(value: boolean) {
        this._borderShadow = value;
        this.setClassIncluded(ComponentStyles.borderShadow, value === true);
    }

    private setElementBorder(which: string) {
        const element = this._getBorderPropTarget();
        let color = this["_border" + which + "Color"];
        if (color != null)
            color = getThemeColor(color);
        element.style["border" + which] = this.getBorderString(this["_border" + which + "Width"], this["_border" + which + "Type"], color);
    }

    protected _getBorderPropTarget(): HTMLElement {
        return this._element;
    }

    public get borderRadius(): string | number {
        return this._borderRadius;
    }

    public set borderRadius(value: string | number) {
        this._borderRadius = value;
        this._element.style.borderRadius = DOMUtil.getSizeSpecifier(value);
    }

    public get borderBottomLeftRadius(): string | number {
        return this._borderBottomLeftRadius;
    }

    public set borderBottomLeftRadius(value: string | number) {
        this._borderBottomLeftRadius = value;
        this._element.style.borderBottomLeftRadius = DOMUtil.getSizeSpecifier(value);
    }

    public get borderBottomRightRadius(): string | number {
        return this._borderBottomRightRadius;
    }

    public set borderBottomRightRadius(value: string | number) {
        this._borderBottomRightRadius = value;
        this._element.style.borderBottomRightRadius = DOMUtil.getSizeSpecifier(value);
    }

    public get borderTopLeftRadius(): string | number {
        return this._borderTopLeftRadius;
    }

    public set borderTopLeftRadius(value: string | number) {
        this._borderTopLeftRadius = value;
        this._element.style.borderTopLeftRadius = DOMUtil.getSizeSpecifier(value);
    }

    public get borderTopRightRadius(): string | number {
        return this._borderTopRightRadius;
    }

    public set borderTopRightRadius(value: string | number) {
        this._borderTopRightRadius = value;
        this._element.style.borderTopRightRadius = DOMUtil.getSizeSpecifier(value);
    }

    private getBorderString(width, type, color: Color) {
        if (width === undefined)
            return "";
        if (width === null)
            return "unset";
        if (color === undefined)
            color = "currentcolor";
        color = getThemeColor(color);
        if (type === undefined)
            type = "solid";
        return width + "px " + type + " " + color;
    }

    public get borderWidth(): number {
        return this._borderWidth;
    }

    public set borderWidth(value: number) {
        this._borderWidth = value;
        this.setElementBorder("");
    }

    public get borderType(): BorderType {
        return this._borderType;
    }

    public set borderType(value: BorderType) {
        this._borderType = value;
        this.setElementBorder("");
    }

    public get borderColor(): Color {
        return this._borderColor;
    }

    public set borderColor(value: Color) {
        this._borderColor = value;
        this.setElementBorder("");
    }

    public get borderBottomWidth(): number {
        return this._borderBottomWidth;
    }

    public set borderBottomWidth(value: number) {
        this._borderBottomWidth = value;
        this.setElementBorder("Bottom");
    }

    public get borderBottomType(): BorderType {
        return this._borderBottomType;
    }

    public set borderBottomType(value: BorderType) {
        this._borderBottomType = value;
        this.setElementBorder("Bottom");
    }

    public get borderBottomColor(): Color {
        return this._borderBottomColor;
    }

    public set borderBottomColor(value: Color) {
        this._borderBottomColor = value;
        this.setElementBorder("Bottom");
    }

    public get borderLeftWidth(): number {
        return this._borderLeftWidth;
    }

    public set borderLeftWidth(value: number) {
        this._borderLeftWidth = value;
        this.setElementBorder("Left");
    }

    public get borderLeftType(): BorderType {
        return this._borderLeftType;
    }

    public set borderLeftType(value: BorderType) {
        this._borderLeftType = value;
        this.setElementBorder("Left");
    }

    public get borderLeftColor(): Color {
        return this._borderLeftColor;
    }

    public set borderLeftColor(value: Color) {
        this._borderLeftColor = value;
        this.setElementBorder("Left");
    }

    public get borderRightWidth(): number {
        return this._borderRightWidth;
    }

    public set borderRightWidth(value: number) {
        this._borderRightWidth = value;
        this.setElementBorder("Right");
    }

    public get borderRightType(): BorderType {
        return this._borderRightType;
    }

    public set borderRightType(value: BorderType) {
        this._borderRightType = value;
        this.setElementBorder("Right");
    }

    public get borderRightColor(): Color {
        return this._borderRightColor;
    }

    public set borderRightColor(value: Color) {
        this._borderRightColor = value;
        this.setElementBorder("Right");
    }

    public get borderTopWidth(): number {
        return this._borderTopWidth;
    }

    public set borderTopWidth(value: number) {
        this._borderTopWidth = value;
        this.setElementBorder("Top");
    }

    public get borderTopType(): BorderType {
        return this._borderTopType;
    }

    public set borderTopType(value: BorderType) {
        this._borderTopType = value;
        this.setElementBorder("Top");
    }

    public get borderTopColor(): Color {
        return this._borderTopColor;
    }

    public set borderTopColor(value: Color) {
        this._borderTopColor = value;
        this.setElementBorder("Top");
    }

    public get margin(): number | string {
        return this._margin;
    }

    public set margin(value: number | string) {
        this._margin = value;
        this._element.style.margin = DOMUtil.getSizeSpecifier(value);
    }

    public get marginTop(): number | string {
        return this._marginTop;
    }

    public set marginTop(value: number | string) {
        this._marginTop = value;
        this._element.style.marginTop = DOMUtil.getSizeSpecifier(value);
    }

    public get marginBottom(): number | string {
        return this._marginBottom;
    }

    public set marginBottom(value: number | string) {
        this._marginBottom = value;
        this._element.style.marginBottom = DOMUtil.getSizeSpecifier(value);
    }

    public get marginLeft(): number | string {
        return this._marginLeft;
    }

    public set marginLeft(value: number | string) {
        this._marginLeft = value;
        this._element.style.marginLeft = DOMUtil.getSizeSpecifier(value);
    }

    public get marginRight(): number | string {
        return this._marginRight;
    }

    public set marginRight(value: number | string) {
        this._marginRight = value;
        this._element.style.marginRight = DOMUtil.getSizeSpecifier(value);
    }

    public get padding(): number {
        return this._padding;
    }

    public set padding(value: number) {
        this._padding = value;
        this._element.style.padding = DOMUtil.getSizeSpecifier(value);
    }

    public get paddingTop(): number {
        return this._paddingTop;
    }

    public set paddingTop(value: number) {
        this._paddingTop = value;
        this._element.style.paddingTop = DOMUtil.getSizeSpecifier(value);
    }

    public get paddingBottom(): number {
        return this._paddingBottom;
    }

    public set paddingBottom(value: number) {
        this._paddingBottom = value;
        this._element.style.paddingBottom = DOMUtil.getSizeSpecifier(value);
    }

    public get paddingLeft(): number {
        return this._paddingLeft;
    }

    public set paddingLeft(value: number) {
        this._paddingLeft = value;
        this._element.style.paddingLeft = DOMUtil.getSizeSpecifier(value);
    }

    public get paddingRight(): number {
        return this._paddingRight;
    }

    public set paddingRight(value: number) {
        this._paddingRight = value;
        this._element.style.paddingRight = DOMUtil.getSizeSpecifier(value);
    }

    public get rowBreak(): boolean {
        return this._rowBreak == null ? true : this._rowBreak;
    }

    public set rowBreak(value: boolean) {
        this._rowBreak = value;
        if (this._parent != null)
            this._parent.childBreakChanged(this);
    }

    public get enabledDuringAdd(): boolean {
        if (this._enabledDuringAdd != null)
            return this._enabledDuringAdd;
        else if (this._boundField?.allowAdd === false)
            return false;
        return true;
    }

    public set enabledDuringAdd(value: boolean) {
        this._enabledDuringAdd = value;
        this._syncEnabled();
    }

    public get enabledDuringUpdate(): boolean {
        if (this._enabledDuringUpdate != null)
            return this._enabledDuringUpdate;
        else if (this._boundField?.allowUpdate === false)
            return false;
        return true;
    }

    public set enabledDuringUpdate(value: boolean) {
        this._enabledDuringUpdate = value;
        this._syncEnabled();
    }

    public get enabledDuringSearch(): boolean {
        if (this._enabledDuringSearch != null)
            return this._enabledDuringSearch;
        else if (this._boundField?.allowSearch === false)
            return false;
        return true;
    }

    public set enabledDuringSearch(value: boolean) {
        this._enabledDuringSearch = value;
        this._syncEnabled();
    }

    public get disabledByServer(): boolean {
        return this._disabledByServer;
    }

    private set disabledByServer(value: boolean) {
        this._disabledByServer = value;
        this._syncEnabled();
    }

    private get defaultEnabled(): boolean {
        const mode = this.getCurrentDataSourceMode();
        if (this.disabledByServer === true && mode !== DataSourceMode.SEARCH)
            return false;
        if ((mode === DataSourceMode.ADD && !this.enabledDuringAdd)
            || (mode === DataSourceMode.UPDATE && !this.enabledDuringUpdate)
            || (mode === DataSourceMode.SEARCH && !this.enabledDuringSearch))
            return false;
        if (this.parent != null)
            return this.parent.enabled; // note that this will be recursive through the parent hierarchy
        return true;
    }

    public get enabled(): boolean {
        return this.defaultEnabled && this._enabled !== false;
    }

    public set enabled(value: boolean) {
        this._enabled = value;
        this._syncEnabled();
    }

    public get license(): string {
        return this._license;
    }

    public set license(value: string) {
        this._license = value;
    }

    get nextFocusable(): string {
        return this._nextFocusable;
    }

    set nextFocusable(value: string) {
        this._nextFocusable = value;
        this.syncTabKeyHandler(value);
    }

    private syncTabKeyHandler(value: string) {
        if (this._nextFocusable == null) {
            this.removeKeyHandler(this._tabKeyHandler);
            this._tabKeyHandler = null;
        } else if (this._tabKeyHandler == null) {
            this._tabKeyHandler = {
                key: Keys.TAB,
                listener: (event) => {
                    const container = this.getRootLayout() as Container;
                    const nextFocusableComp = container?.findComponentById(value) as Component;
                    nextFocusableComp?.focus();
                },
                element: this._element,
                scope: this._element
            }
            this.addKeyHandler(this._tabKeyHandler);
        }
    }

    public get visible(): boolean {
        const mode = this.getCurrentDataSourceMode();
        return this._visible !== false &&
            (mode == null
                || mode === DataSourceMode.NONE
                || (mode === DataSourceMode.ADD && this.visibleDuringAdd)
                || (mode === DataSourceMode.SEARCH && this.visibleDuringSearch)
                || (mode === DataSourceMode.UPDATE && this.visibleDuringUpdate));
    }

    public set visible(value: boolean) {
        this._visible = value;
        this._syncVisible();
    }

    public get visibleDuringAdd(): boolean {
        return this._visibleDuringAdd == null ? true : this._visibleDuringAdd;
    }

    public set visibleDuringAdd(value: boolean) {
        this._visibleDuringAdd = value;
        this._syncVisible();
    }

    public get visibleDuringSearch(): boolean {
        return this._visibleDuringSearch == null ? true : this._visibleDuringSearch;
    }

    public set visibleDuringSearch(value: boolean) {
        this._visibleDuringSearch = value;
        this._syncVisible();
    }

    public get visibleDuringUpdate(): boolean {
        return this._visibleDuringUpdate == null ? true : this._visibleDuringUpdate;
    }

    public set visibleDuringUpdate(value: boolean) {
        this._visibleDuringUpdate = value;
        this._syncVisible();
    }

    public get preventCollapse(): boolean {
        return this._preventCollapse == null ? false : this._preventCollapse;
    }

    public set preventCollapse(value: boolean) {
        this._preventCollapse = value;
        this._syncVisible();
    }

    protected _syncVisible() {
        this.setClassIncluded(this._preventCollapse ?
            ComponentStyles.hiddenNoCollapse :
            ComponentStyles.hidden, !this.visible && this.__designer == null);
    }

    public get hiddenUnlessMouseOver(): Component {
        return this._hiddenUnlessMouseOver;
    }

    public set hiddenUnlessMouseOver(value: Component) {
        if (this._hiddenUnlessMouseOver != null && this._hiddenChildComponentMouseEnterListener != null) {
            this._hiddenUnlessMouseOver.removeMouseEnterListener(this._hiddenChildComponentMouseEnterListener);
            this._hiddenUnlessMouseOver.removeMouseLeaveListener(this._hiddenChildComponentMouseLeaveListener);
        }
        this._hiddenUnlessMouseOver = value;
        if (value != null) {
            this.style.opacity = "0";
            this._hiddenChildComponentMouseEnterListener = () => this.style.opacity = "1";
            value.addMouseEnterListener(this._hiddenChildComponentMouseEnterListener);
            this._hiddenChildComponentMouseLeaveListener = () => this.style.opacity = "0";
            value.addMouseLeaveListener(this._hiddenChildComponentMouseLeaveListener);

            // if the component is removed from the DOM while the mouse is over the _hiddenUnlessMouseOver component,
            // we need to make sure this component is hidden in case it is added back to the DOM
            const addMountListeners = () => {
                if (this.hiddenUnlessMouseOver == null)
                    return;
                this.addUnmountListener(() => {
                    if (this.hiddenUnlessMouseOver != null) {
                        this.style.opacity = "0";
                        this.hiddenUnlessMouseOver.addMountListener(() => {
                            addMountListeners();
                        })
                    }
                })
            }
            addMountListeners();
        }
    }

    public get zIndex(): number {
        return this._zIndex;
    }

    public set zIndex(value: number) {
        this._zIndex = value;
        if (value == null)
            this._element.style.zIndex = "";
        else
            this._element.style.zIndex = value.toString();
    }

    public getEffectiveZIndex(): number {
        let result = this.zIndex;
        if (result == null) {
            const ancestorValue = DOMUtil.getStyleValueFromElementOrAncestor("z-index", this._element);
            if (typeof ancestorValue === "string")
                result = DOMUtil.convertStyleAttrToNumber(ancestorValue);
            else
                result = ancestorValue;
        }
        return result;
    }

    public get isRow(): boolean {
        return this._isRow;
    }

    public set isRow(value: boolean) {
        this._isRow = value;
    }

    public toggleCollapsed() {
        const rect = this._element.getBoundingClientRect();
        rect.height > 10 ? this.collapse() : this.expand();
    }

    public collapse(options?: TransitionOptions) {
        return TransitionsUtil.collapseComponent(this, options);
    }

    public collapseNow() {
        TransitionsUtil.collapseComponentNow(this);
    }

    public expand(options?: TransitionOptions): Promise<any> {
        return TransitionsUtil.expandComponent(this, options);
    }

    public expandNow() {
        TransitionsUtil.expandComponentNow(this);
    }

    public slideInAndFill(fillWidth: boolean = true, fillHeight: boolean = true, options?: TransitionOptions, displayOverlay?: boolean, overlayProps?: Partial<OverlayProps>): Promise<any> {
        //if we are displaying the slideout inside an overlay that already takes the height of the page header into account, we don't need to adjust
        //for the page header height a second time.
        const topAdjust = ((displayOverlay === true && overlayProps?.coverPageHeader === false) ? MainPageAccessor.pageHeaderHeight : 0);
        this.fillWindowHeightAndWidth(fillWidth, fillHeight, topAdjust);
        return this.slideIn(options, displayOverlay, overlayProps);
    }

    public fillWindowHeightAndWidth(fillWidth: boolean = true, fillHeight: boolean = true, topAdjust: number = 0) {
        if (fillWidth === true)
            this.fillWindowWidth();
        if (fillHeight === true)
            this.fillWindowHeight(topAdjust);
    }

    public fillWindowWidth() {
        this.width = "100%";
    }

    public fillWindowHeight(topAdjust: number = 0) {
        this.height = this.componentBehavior.fillWindowHeight(topAdjust);
    }

    public slideIn(options?: TransitionOptions, displayOverlay?: boolean, overlayProps?: Partial<OverlayProps>): Promise<any> {
        this._slideOutOverlay = null;
        this._slideOutRemovalOptions = { ...options };
        if (displayOverlay === true) {
            if (overlayProps == null)
                overlayProps = {};
            if (overlayProps.closeHandler == null) //beware...if you provide this you must also handle closing the slideout
                overlayProps.closeHandler = () => this.slideOut(options, overlayProps?.componentToFocusOnClose);
            this._slideOutOverlay = DynamicLoader.getModuleByName("components/page/Overlay").Overlay.showInOverlay(this, overlayProps);
        }

        const cls = DynamicLoader.getModuleByName("components/base/SlideoutSizeManager");
        new cls.SlideoutSizeManager(this, options?.direction);
        return TransitionsUtil.slideInComponent(this, options);
    }

    public slideOut(options?: TransitionOptions, componentToFocusOnClose?: Component): Promise<any> {
        const optionsToUse = options == null ? this._slideOutRemovalOptions : options;
        const result = TransitionsUtil.slideOutComponent(this, optionsToUse).then(() => {
            if (this._slideOutOverlay != null) {
                DynamicLoader.getModuleByName("components/page/Overlay").Overlay.hideOverlay(this._slideOutOverlay, componentToFocusOnClose);
                this._slideOutOverlay = null;
            }
            this._slideOutRemovalOptions = null;
        });
        return result;
    }

    public get field(): string {
        return this._field;
    }

    public set field(value: string) {
        const oldValue = this._field;
        this._field = value;
        this._matchIdToValue(oldValue, value);
        this._syncDatabinding();
        this._dataSource?.updateComponentsByField(this, oldValue);
    }

    /**
     * When a component is bound to multiple fields, override this method to return the list of fields this component is bound to.
     * This is currently (10/2021) used in DataSource fieldUpdateListener to dynamically update fields that are displayed more than
     * once on a page (i.e. when one use of a field is updated, all components that display that field are re-displayed).
     */
    getFieldNames(): string[] {
        const field = this.field;
        if (field == null)
            return null;
        else
            return [field];
    }

    public get sortField(): string {
        return this._sortField != null ? this._sortField : this.field;
    }

    public set sortField(value: string) {
        this._sortField = value;
    }

    public get excludeFromSortFields(): string[] {
        return this._excludeFromSortFields;
    }

    public set excludeFromSortFields(value: string[]) {
        this._excludeFromSortFields = value;
    }

    public get excludeFromAutogenLayout(): boolean {
        return this._excludeFromAutogenLayout;
    }

    public set excludeFromAutogenLayout(value: boolean) {
        this._excludeFromAutogenLayout = value;
    }

    /**
     * When a component is bound to multiple fields, override this method to return the list of fields that should be considered within Table sorting
     *
     * @returns string[]
     */
    getSortFieldNames(): string[] {
        const field = this.sortField;
        if (field == null)
            return null;
        else
            return this.filterSortFields([field]);
    }

    get sortDescendingByDefault(): boolean {
        return this._sortDescendingByDefault || false;
    }

    set sortDescendingByDefault(value: boolean) {
        this._sortDescendingByDefault = value;
    }

    get sortNullsAtEnd(): boolean {
        return this._sortNullsAtEnd || false;
    }

    set sortNullsAtEnd(value: boolean) {
        this._sortNullsAtEnd = value;
    }

    filterSortFields(sortFields: string[]): string[] {
        if (ArrayUtil.isEmptyArray(sortFields) || ArrayUtil.isEmptyArray(this.excludeFromSortFields))
            return sortFields;
        for (const excludeField of this.excludeFromSortFields) {
            ArrayUtil.removeFromArray(sortFields, excludeField);
        }
        return sortFields;
    }

    protected _matchIdToValue(oldValue: string, value: string, idPrefix: string = this.idPrefix) {
        if (this.__designer?.redisplayProp == null)
            return;
        if (StringUtil.isEmptyString(value)) {
            if (StringUtil.isEmptyString(this.id) && this.__designer["getNextNumber"] != null) {
                this.id = idPrefix + this.__designer["getNextNumber"](idPrefix);
                this.__designer.redisplayProp("id", this.id);
            }
            return;
        }
        oldValue = ComponentUtil.removeSpecialIdChars(oldValue);
        value = ComponentUtil.removeSpecialIdChars(value);
        const newId = StringUtil.toLowerCamelCase(idPrefix + "_" + value);
        if (value != null && this.id !== newId && this.hasDefaultDesignerId(idPrefix)) {
            this.id = newId;
            this.__designer.redisplayProp("id", this.id);
        }
    }

    hasDefaultDesignerId(idPrefix: string = this.idPrefix) {
        if (!this.id.startsWith(idPrefix))
            return false;
        const afterType = this.id.substring(idPrefix.length)
        return afterType.length === 0 || !isNaN(Number(afterType))
    }

    get idPrefix(): string {
        return this.serializationName;
    }

    get typeName(): string {
        if (this.serializationName == null) {
            log.info(this, "Component needed to override serializationName", this);
            throw new Error("Could not find component type for " + this);
        }
        return this.serializationName;
    }

    protected _syncEnabled() {
        this._applyEnabled(this.enabled);
    }

    /**
     * Component subclasses need to override this to enable/disable
     * their DOM elements in response to the various enabled flags changing
     * @param value
     */
    _applyEnabled(value: boolean): void {
    }

    /**
     * Shared method for subclasses call in their _applyEnabled method to apply the enabled state to the DOM element
     * @param value
     */
    protected commonApplyEnabled(value: boolean) {
        if (value) {
            this._element.removeAttribute("disabled");
            this.color = this.color;
            this.backgroundColor = this.backgroundColor;
            this.borderColor = this.borderColor;
            this.cursor = this.cursor;
            if (!StringUtil.isEmptyString(this._element.style.backgroundColor) && this instanceof Button) {
                this._element.style.opacity = "1";
            }
        }
        else {
            if (this.__designer == null)
                this._element.setAttribute("disabled", "true");
            if (!StringUtil.isEmptyString(this._element.style.backgroundColor) && this instanceof Button) {
                this._element.style.opacity = "0.5";
            } else {
                this._element.style.color = getThemeColor("subtle.light");
            }
            this._element.style.cursor = "";
        }
    }

    protected _syncDatabinding() {
        if (this.dataSource != null && this.field != null) {
            this.metaDataPromise = this.metaDataPromise.then(async () => {
                const metadata = await this.dataSource.getMetadata();
                this._metadataChanged(metadata);
            })
        } else if (this.boundRow != null && this.field != null) {
            // Can get rid of this someday, when the PropertiesTable deals in real ModelRows.
            // Still need to check for a modelpath though.
            if (this.boundRow instanceof ModelRow) {
                // if (StringUtil.isEmptyString(this.boundRow._modelPath))
                this._metadataChanged(this.boundRow.getMetadata());
            }
        }
    }

    protected _metadataChanged(metadata: ApiMetadata) {
        if (metadata != null && this.field != null) {
            this._boundField = metadata.output[this.field];
            if (this._boundField == null)
                this._boundField = metadata.input[this.field];
            if (this._boundField == null)
                this._boundField = new MetadataField({ name: this.field });
            this.syncCaptionIfPossible();
            this._fieldBindingChanged();
        }
    }

    /**
     * This method is meant to be overridden if a subclass wants to take special action when it is bound to a field and we have retrieved the metadata for that field
     * from the server.
     *
     */
    protected _fieldBindingChanged() {
    }

    public get searchOnly(): boolean {
        if (this._searchOnly != null)
            return this._searchOnly;
        // Not every component keeps the searchOnly property...for example layouts remove all 'Data' properties
        const property = this.getPropertyDefinitions().searchOnly;
        return property?.defaultValue ?? false;
    }

    public set searchOnly(value: boolean) {
        if (this.searchOnly === value)
            return;
        const oldValue = this.searchOnly;
        this._searchOnly = value;
        this._dataSource?.addBoundComponent(this, oldValue);
        this.syncRequiredPropsInDesigner();
    }

    public get dataSource(): DataSource {
        return this._dataSource;
    }

    public set dataSource(value: DataSource) {
        if (this._dataSource != null && this.dataSource instanceof DataSource) {
            this._dataSource.removeBoundComponent(this);
            this._dataSource.removeHasChangedComponent(this);
        }
        this._dataSource = value;
        if (this._dataSource != null && this.dataSource instanceof DataSource)
            this._dataSource.addBoundComponent(this);
        this.syncHasChangedComponent();
        this._syncDatabinding();
        this.syncCaptionIfPossible();
    }

    displayData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        if (this.shouldDisplayData(data, allData, rowIndex) !== true) {
            return;
        }

        this.displayComponentData(data, allData, rowIndex);
        this.fireDataDisplayListeners(data, allData, rowIndex);
    }

    protected displayComponentData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
        // Component subclasses should typically override this, display their data and then call super.displayComponentData() afterwards
    }

    shouldDisplayData(data: ModelRow, allData: ModelRow[], rowIndex: number): boolean {
        if (this._designer != null)
            return false;

        if (this._boundField != null) {
            if (this.boundRow != null)
                return this.boundRow === data;

            if (this.dataSource != null)
                return this.dataSource.activeRow === data;

            const tableRow = TableRow.getContainingTableRow(this);
            if (tableRow != null) {
                return tableRow.data === data;
            }
        }
        return true;
    }

    /**
     * This method defines any component-specific behavior that needs to happen when the component is searchOnly and is
     * part of a form, and then the ResetFormButton is invoked.  Specifically, this method should handle state that is
     * not related to the data the component is rendering.
     */
    resetNonDataState() {
    }

    /**
     * Defines if the component can grow to the height of the container that contains it.
     * The default implementation of this method does nothing, so the component doesn't grow.
     *
     * @param height : number the height to use to reset the component's height (provided as a number)
     */
    public growToContainerHeight(height: number) { }

    /**
     * This method is called for all Components on a Layout as it is loaded.  It allows the Component to make sure that any metadata that
     * it may need is loaded before the layout initialization finishes.
     * A good example of this is Textbox.lookupModel.
     */
    public async loadMetadata(): Promise<void> {
        await this.metaDataPromise;
    }

    // temporary accessor until everything is converted to TS
    // ideally we want to delete this but, even after conversion to TS, there are lots of uses of it
    /**
     * @deprecated
     */
    public get element(): HTMLElement {
        return this._element;
    }

    public set element(value: HTMLElement) {
        this._element = value;
    }

    async _deserializeSpecialProps(props: DeserializeProps): Promise<string[]> {
        return this.setDataSourceFromDeserializeProps(props);
    }

    /**
     * Override this method in subclasses to invoke logic to occur after the component has been created via
     * deserialization.
     */
    doAfterDeserialize() {
    }

    /**
     * Override this method in subclasses to invoke logic to occur after the component has been created via cloning.
     */
    doAfterClone() {
    }

    getPropertyValuesForCloning(): Partial<ComponentProps> {
        const result: Partial<ComponentProps> = {};
        const props = this.getPropertyDefinitions();
        for (const key in props) {
            const value = this[key];
            if (value !== undefined)
                result[key] = this[key];
        }
        return result;
    }

    protected setDataSourceFromDeserializeProps(props: DeserializeProps): string[] {
        const datasourceId = props.def.dataSource;
        if (datasourceId != null && props.dataSources != null)
            this.dataSource = props.dataSources[datasourceId];
        return ["dataSource"];
    }

    get parent(): Container {
        return this._parent;
    }

    set parent(value: Container) {
        this._parent = value;
    }

    public get requiredDuringAdd(): boolean {
        return (this.searchOnly !== true) ? this._requiredDuringAdd ?? this.getDefaultRequired(DataSourceMode.ADD)
            : false;
    }

    public set requiredDuringAdd(value: boolean) {
        if (this._requiredDuringAdd !== value) {
            this._requiredDuringAdd = value;
            this._syncRequired();
        }
    }

    public get requiredDuringSearch(): boolean {
        return this._requiredDuringSearch ?? this.getDefaultRequired(DataSourceMode.SEARCH);
    }

    public set requiredDuringSearch(value: boolean) {
        if (this._requiredDuringSearch !== value) {
            if (this.searchOnly === true)
                this.required = value;
            else
                this._requiredDuringSearch = value;
            this._syncRequired();
        }
    }

    public get requiredDuringUpdate(): boolean {
        return (this.searchOnly !== true) ? this._requiredDuringUpdate ?? this.getDefaultRequired(DataSourceMode.UPDATE)
            : false;
    }

    public set requiredDuringUpdate(value: boolean) {
        if (this._requiredDuringUpdate !== value) {
            this._requiredDuringUpdate = value;
            this._syncRequired();
        }
    }

    get required(): boolean {
        if (this.getDefaultRequired() !== true) {
            return false;
        }
        switch (this.getCurrentDataSourceMode()) {
            case DataSourceMode.ADD:
                return this.requiredDuringAdd;
            case DataSourceMode.SEARCH:
                return this.requiredDuringSearch;
            case DataSourceMode.UPDATE:
                return this.requiredDuringUpdate;
            default:
                return true;
        }
    }

    set required(value: boolean) {
        if (this._required !== value) {
            this._required = value;
            this.syncRequiredPropsInDesigner();
            this._syncRequired();
        }
    }

    private syncRequiredPropsInDesigner() {
        if (this._designer != null) {
            this._requiredDuringAdd = undefined;
            this._requiredDuringUpdate = undefined;
            this._requiredDuringSearch = undefined;
        }
    }

    private getDefaultRequired(mode?: DataSourceMode): boolean {
        if (mode === DataSourceMode.SEARCH)
            return this.getDefaultRequiredSearch();
        else if (this.searchOnly === true) {
            if (mode === DataSourceMode.ADD || mode === DataSourceMode.UPDATE)
                return false;
            return this.getDefaultRequiredSearch();
        }
        return this.getDefaultRequiredNonSearch();
    }

    private getDefaultRequiredNonSearch(): boolean {
        return this._required ?? this._boundField?.required ?? false;
    }

    private getDefaultRequiredSearch(): boolean {
        if (this.searchOnly === true)
            return this._required ?? this._boundField?.requiredSearch ?? false;
        return this._boundField?.requiredSearch ?? false;
    }

    protected _syncRequired() {
        this.syncCaptionIfPossible();
    }

    syncCaptionIfPossible() {
        if (typeof this["syncCaption"] === "function") {
            this["syncCaption"]();
        }
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "required") {
            if (this.searchOnly !== true)
                return this._boundField?.required ?? false;
            return this._boundField?.requiredSearch ?? false
        }
        else if (prop.name === "requiredDuringAdd")
            return this.getDefaultRequired(DataSourceMode.ADD);
        else if (prop.name === "requiredDuringSearch")
            return this.getDefaultRequired(DataSourceMode.SEARCH);
        else if (prop.name === "requiredDuringUpdate")
            return this.getDefaultRequired(DataSourceMode.UPDATE);
        else if (prop.name === "enabledDuringAdd")
            return this._boundField == null || this._boundField.allowAdd !== false;
        else if (prop.name === "enabledDuringSearch")
            return this._boundField == null || this._boundField.allowSearch !== false;
        else if (prop.name === "enabledDuringUpdate")
            return this._boundField == null || this._boundField.allowUpdate !== false;
        else if (prop.name === "enabled")
            return this.defaultEnabled;
        else if (prop.name === "sortField")
            return this.field;
        else if (prop.name === "displayLabel")
            return this["caption"] || this._boundField?.caption;
        return super.determinePropertyDefaultValue(prop);
    }

    getUniqueIdentifier() {
        const route = DynamicLoader.getRouteFromURL();
        const id = this.id;
        if (route == null || id == null)
            return null;
        return route + ":" + id;
    }

    get valueAsString(): string {
        return null;
    }

    set valueAsString(value: string) {
    }

    storeUserChoiceIfRemembered() {
        if (this.rememberUserChoice === true && this._designer == null) {
            const id = this.getUniqueIdentifier();
            if (id == null)
                log.error("Could not store user choice because component has no unique identifier", this);
            else {
                const key = id + ".value";
                const value = this.valueAsString;
                LocalUserSettings.set(key, value);
            }
        }
    }

    restoreUserChoice() {
        const id = this.getUniqueIdentifier();
        if (id == null)
            log.error("Could not restore user choice because component has no unique identifier", this);
        else {
            const key = id + ".value";
            const value = LocalUserSettings.getString(key);
            if (value != null)
                this.valueAsString = value;
        }
    }

    get rememberUserChoice(): boolean {
        return this._rememberUserChoice == null ? false : this._rememberUserChoice;
    }

    set rememberUserChoice(value: boolean) {
        this._rememberUserChoice = value;
        if (value && !this.isMounted && this._designer == null)
            this.addMountListener(() => this.restoreUserChoice());
    }

    get isMounted(): boolean {
        return document.body.contains(this._element);
    }

    public dataSourceModeChanged(mode: DataSourceMode) {
        this._syncEnabled();
        this._syncVisible();
        this._syncRequired();
        this.resetValidation();
    }

    public applyDefaultDataValue() {
        if (this.defaultDataValue && this.field)
            this.getRelevantModelRow()?.set(this.field, this.defaultDataValue);
    }

    public updateBoundData(row: ModelRow, mode: DataSourceMode, fillingLinkedValues?: boolean) {
    }

    /**
     * Sets the key of the user's selected theme.  All the properties from the theme object will
     * be applied to this Component.  This is currently a set-only property so that we don't store
     * the key in the Component's object and use memory at such a low level.  If we ever have a
     * need to read the themeKey, we can evaluate that again.
     */
    public set themeKey(value: string) {
        if (value != null) {
            const themeObject = getThemeForKey(value);
            if (themeObject == null) {
                if (this.__designer == null)
                    log.error(() => ["Couldn't find theme for key", value, "Component", this, "Theme", getTheme()]);
            } else
                this.applyTheme(themeObject);
        }
    }

    public applyTheme(themeObject: object, force = false) {
        log.debug(this, () => ["Applying theme object to", this, "Theme", themeObject]);
        for (const key in themeObject) {
            if (force || !this.isPropSet(key)) {
                log.debug(this, () => ["Setting prop", key, "to theme value", themeObject[key]]);
                this[key] = themeObject[key];
            }
        }
    }

    /**
     * Override this for components that return a default value for a property when it is unset.  Currently,
     * this allows the logic that applies theme properties to know whether or not a component has specifically
     * set a property value.
     */
    protected isPropSet(propName: string) {
        return this[propName] != null;
    }

    public containsText(searchValue: string, ignoreCase: boolean = true): boolean {
        if (StringUtil.isEmptyString(searchValue))
            return true;
        if (ignoreCase)
            searchValue = searchValue.toLowerCase();
        const searchValues = this.getSearchValues();
        if (searchValues != null)
            for (let value of searchValues) {
                if (ignoreCase)
                    value = value?.toLowerCase();
                if (value != null && value.indexOf(searchValue) >= 0)
                    return true;
            }
        return false;
    }

    public getSearchValues(): string[] {
        return [];
    }

    public getDesigner() {
        return this._designer;
    }

    public setDesigner(designer: any) {
        this._designer = designer;
    }

    public addListenersToAll(listeners: any) {
        if (listeners != null) {
            for (const key of Object.keys(listeners)) {
                const listenerInfo = listeners[key];
                const listenerDef = listenerInfo.def;
                const listenerList = listenerInfo.listeners;
                for (const listener of listenerList) {
                    this.addEventListener(listenerDef, listener)
                }
            }
        }
    }

    public addMountListener(listener: () => void): void {
        MountUtil.addMountListener(this._element, listener);
    }

    public addUnmountListener(listener: () => void): void {
        MountUtil.addUnmountListener(this._element, listener);
    }


    public addResizeListener(listener: ResizeListener): void {
        this.addEventListener(ComponentListenerDefs.resize, listener);
        this.syncResizeObserver();
    }

    public removeResizeListener(listener: ResizeListener): void {
        this.removeEventListener(ComponentListenerDefs.resize, listener);
        this.syncResizeObserver();
    }

    private syncResizeObserver() {
        const listenerCount = this.getListeners(ComponentListenerDefs.resize)?.length;
        if (listenerCount > 0 && this.resizeObserver == null) {
            log.debug(this, () => ["Add resize observer", this]);
            const debouncedResized =  JSUtil.debounce(() => this.resized(), 100);
            this.resizeObserver = new ResizeObserver(debouncedResized);
            this.resizeObserver.observe(this._element);
        } else if (listenerCount === 0 && this.resizeObserver != null) {
            log.debug(this, () => ["Remove resize observer", this]);
            this.resizeObserver.unobserve(this._element);
            this.resizeObserver.disconnect();
            delete this.resizeObserver;
        }
    }

    protected resized() {
        log.debug(this, () => ["Resized", this]);
        const listeners = this.getListeners(ComponentListenerDefs.resize);
        if (listeners != null) {
            const event = new ResizeEvent(this);
            for (const listener of listeners.listeners)
                listener(event);
        }
    }

    get boundRow(): any {
        return this._boundRow;
    }

    set boundRow(row: any) {
        if (this.boundRow === row)
            return;
        this._boundRow = row;
        this._syncDatabinding();
    }

    get serializationName(): string {
        return undefined;
    }

    /**
     * Specifies the proper/pretty name for the component.  Every component should override this method.
     */
    get properName(): string {
        throw new Error("Every component must specify its proper name");
    }

    /**
     * This returns the position of the Component on the page (underlying HTMLElement's getBoundingClientRect()) or null
     * if the Component has not been added to the page yet.
     */
    get bounds(): DOMRect {
        const result = this._element.getBoundingClientRect();
        if (result?.x === 0 && result?.y === 0 && result?.width === 0 && result?.height === 0)
            return null;
        return result;
    }

    /**
     * Returns true if the point represented by x,y is over this Component.  Usually this is used in conjunction with MouseEvent
     * xOffset and yOffset to determine if the mouse is over a Component.
     *
     * @param x
     * @param y
     * @returns
     */
    containsPoint(x: number, y: number): boolean {
        const bounds = this.bounds;
        if (this.bounds == null)
            return false;
        return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
    }

    public hasBoundField(): boolean {
        return this._boundField != null;
    }

    /**
     * Reattaches instances of EventListenerList to the target element (as defined by getEventTarget()).
     *
     * This is necessary when a Component exists, but its target element has been thrown away and needs to be recreated.
     * A common scenario occurs when a Component that can be rendered as printable has been made printable, and is
     * subsequently being toggled back to non-printable.  When this occurs, the Component contains its EventListenerLists
     * throughout, but those events need to be re-attached to the new target DOM element (that is created when the
     * component is being converted back to non-printable).
     *
     * To do this, this method calls EventListenerList.syncDomEvent() each EventListenerList that the Component contains.
     */
    reattachListeners() {
        const listenerDefs = this.getListenerDefs();
        for (const def of Object.values(listenerDefs)) {
            this[def.listName]?.syncDomEvent();
        }
        this.syncContextMenuListeners();
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...ComponentListenerDefs,
            "dataDisplay": { ..._dataDisplayListenerDef },
            "contextMenuCreation": { ..._contextMenuCreationListenerDef }
        };
    }

    /**
     * Gets the value of the field as it was when the field first got focus.
     *
     * @returns any
     */
    get preFocusValue(): any {
        return this._preFocusValue;
    }

    /**
     * Saves the value of the field at the time of the field gaining focus.
     * This value can be used later to determine if the field changed while it had focus.
     */
    private _setPreFocusValue(event: Event) {
        if (this._element.contains(event.domEvent["relatedTarget"])) {
            log.debug(this, "Not setting pre-focus value for component ID %o, the element gaining focus is within this same component", this.id);
            return;
        }
        this._preFocusValue = this.getDataValue();
        log.debug(this, "Set pre-focus value: %o", this._preFocusValue);
    }

    getRelevantModelRow(): ModelRow {
        return DynamicLoader.getModuleByName("components/base/ComponentDataLink").getRelevantModelRow(this);
    }

    getCurrentDataSourceMode(): DataSourceMode {
        return DynamicLoader.getModuleByName("components/base/ComponentDataLink").getCurrentDataSourceMode(this);
    }

    inDataSourceMode(...modes: DataSourceMode[]): boolean {
        const currentMode = this.getCurrentDataSourceMode();
        return currentMode != null && modes.some(mode => mode === currentMode);
    }

    /**
     * Gets the value of the field.  This could be:
     * - In the case of a data-bound component, this will be the value backing the field (taken from the ModelRow).
     * - In the case of a component that is not data-bound, this will be the value that best indicates the state of the field.
     *
     * @returns any
     */
    getDataValue(): any {
        const boundValue = this.getRelevantModelRow()?.get(this._field, null);
        if (boundValue != null)
            return boundValue;
        return this.getBasicValue();
    }

    /**
     * Method to be overridden in each sub-class of Component, which will return the Component's value when it is not data-bound.
     *
     * @returns any
     */
    getBasicValue(): any {
        return null;
    }

    changedWhileFocused(): boolean {
        const valueOnBlur = this.getDataValue()
        if (this.preFocusValue == null && valueOnBlur == null)
            return false;
        else
            return this.preFocusValue !== valueOnBlur;
    }

    public get displayLabel(): string {
        return this._displayLabel || this._boundField?.caption;
    }

    public set displayLabel(value: string) {
        this._displayLabel = value;
    }

    /**
     * This returns a string that describes the given field. Most Components that just display a single field
     * will just return the Component's regular displayLabel property.  Components that might display multiple fields,
     * like CityState might choose to have separate displayLabel properties for each field that they display
     * (or in CityState's case, we might choose to append some text ("City", "State", "Zip") to the displayLabel).
     */
    public getDisplayLabel(field: string, context: string): string {
        return this.displayLabel;
    }

    async ripple(color?: string, options?: Partial<RippleOptions>): Promise<void> {
        color = getThemeColor(color);
        const speed = options?.speed || 450;
        const button = this._element;
        const ripple = document.createElement("span");
        ripple.className = "cmp-ripple";
        ripple.style.animation = "rippleEffect " + speed + "ms linear";
        ripple.style.backgroundColor = color ? color : "color-mix(in srgb, currentColor 35%, transparent)";
        const thisStyle = getComputedStyle(this._element);
        button.appendChild(ripple);
        const rippleWrapper = document.createElement("span");
        if (thisStyle.borderRadius != null) {
            rippleWrapper.style.borderRadius = thisStyle.borderRadius;
            ripple.style.borderRadius = thisStyle.borderRadius;
        }
        rippleWrapper.className = "cmp-rippleWrapper"
        rippleWrapper.appendChild(ripple)
        this._element.appendChild(rippleWrapper);
        return JSUtil.delayPromise(speed).then(() => {
            rippleWrapper?.parentNode?.removeChild(rippleWrapper);
        });
    }

    addUnderlineWhenMouseOver() {
        if (this != null) {
            this.addMouseEnterListener(event => {
                this.underlineWhenMouseOver(event.target, true);
            });
            this.addMouseLeaveListener(event => {
                this.underlineWhenMouseOver(event.target, false);
            });
        }
    }

    underlineWhenMouseOver(comp: Component, isOver: boolean) {
        comp.setClassIncluded(LabelStyles.link, isOver);
    }

    get keyHandlers(): KeyHandler[] {
        return this._keyHandlers;
    }

    set keyHandlers(value: KeyHandler[]) {
        this._keyHandlers = value;
    }

    addKeyHandler(handler: KeyHandler) {
        if (this.keyHandlers == null)
            this.keyHandlers = [];
        this.keyHandlers.push(handler);
    }

    removeKeyHandler(handler: KeyHandler) {
        ArrayUtil.removeFromArray(this.keyHandlers, handler);
        if (ArrayUtil.isEmptyArray(this.keyHandlers))
            this.keyHandlers = null;
    }

    addHandlerForKey(key: string, modifiers?: ModifierKeys, listener?: (event) => void) {
        this.addKeyHandler(this.createKeyHandler(key, listener, modifiers));
    }

    getKeyHandlers(): KeyHandler[] {
        return this._keyHandlers;
    }

    createKeyHandler(key: string, listener: (event) => void, modifiers?: ModifierKeys, scope?: HTMLElement): KeyHandler {
        return { key: key, modifiers: modifiers, listener: listener, element: this._element, scope: scope };
    }

    /**
     * This method returns the permissions types that this Component can handle.  The base Component class
     * can handle 'view' permissions and Component subclasses can override this to return the
     * permissions types that they are capable of handling.
     */
    getPermissionsTypes(): PermissionsDefinition[] {
        const result = [PermDefinitionConstants.VIEW_SECURITY];

        if (this.supportsEditSecurity) {
            result.push(PermDefinitionConstants.EDIT_SECURITY);
        }

        return result;
    }

    public isOrContains(anotherComponent: Component): boolean {
        return DOMUtil.isOrContains(this._element, anotherComponent?._element);
    }

    getRootLayout(): Layout {
        const parents: Container[] = [];
        this.addRecursiveParents(this, parents);
        return parents.reverse().find(container => container instanceof Layout) as Layout;
    }

    getParentLayout(): Layout {
        const parents: Container[] = [];
        this.addRecursiveParents(this, parents);
        return parents.find(container => container instanceof Layout) as Layout;
    }

    /**
     * This method can be overridden in subclasses to update some state in the component when the enclosing, outermost
     * layout has finished loading.  It should not be called directly from non-component classes.
     */
    _doAfterOutermostLayoutLoaded() {
    }

    private addRecursiveParents(component: Component, result: Container[]) {
        if (component.parent != null) {
            // check for circular hierarchy to avoid an infinite recursion loop
            if (result.includes(component.parent))
                return [];
            result.push(component.parent)
            this.addRecursiveParents(component.parent, result);
        }
    }

    findParentWithId(parentId: string): Container {
        if (this.parent == null)
            return null;
        if (this.parent.id === parentId)
            return this.parent;
        return this.parent.findParentWithId(parentId);
    }

    findParentOfType(parentSerializationName: string): Component {
        if (this.parent == null)
            return null;
        if (this.parent.serializationName === parentSerializationName)
            return this.parent;
        return this.parent.findParentOfType(parentSerializationName);
    }

    findAllParentsOfType(...types: ComponentTypeTestValue[]):
        Component[] {
        const filterPredicate = (parent: Container) => {
            return types.some(validType => {
                return (typeof validType === "string") ? parent.serializationName === validType :
                    parent instanceof validType;
            });
        }
        return this.findAllParents(filterPredicate);
    }

    findAllParents(filterPredicate: (parent: Container) => boolean): Component[] {
        const parents: Container[] = [];
        this.addRecursiveParents(this, parents);
        return parents.reverse().filter(filterPredicate);
    }

    override syncBaseVersionProps() {
        super.syncBaseVersionProps();
        if (this._designer != null)
            this.setClassIncluded(DesignerStyles.modifiedBaseComponent, this.baseVersionProps != null);
    }

    public get doOnDataSourceChanged(): (dataSource: DataSource, changesPresent: boolean) => void {
        return this._doOnDataSourceChanged;
    }

    public set doOnDataSourceChanged(value: (dataSource: DataSource, changesPresent: boolean) => void) {
        if (value === this._doOnDataSourceChanged)
            return;
        this._doOnDataSourceChanged = value;
        this.syncHasChangedComponent();
    }

    private syncHasChangedComponent() {
        if (this.dataSource != null && this.dataSource instanceof DataSource) {
            if (this._doOnDataSourceChanged != null)
                this.dataSource.addHasChangedComponent(this);
            else
                this.dataSource.removeHasChangedComponent(this);
        }
    }

    /**
     * Indicates if the component has a value.  It should be overridden in each Component class, so that each
     * component can define its own logic for determining if a value is present.
     * @returns boolean
     */
    public isEmpty(): boolean {
        //it would be nice if we threw an error to help us identify places where this could be implemented,
        //but that breaks existing screens
        //throw new Error("isEmpty() has not been implemented for this component");
        return false;
    }

    /**
     * This method should be overriden in classes that can contain a component that can be enlarged.  It allows that
     * panel/container to remove/hide all other components, so that the enlarging component(s) have room to grow.
     *
     * This method is defined here (and not in Container) because of classes like TableRow, which can contain other
     * components, but do not descend from Container.
     * @param componentsToEnlarge An array of Components that will be enlarged once the parent panel/container is ready
     */
    public doBeforeComponentEnlarge(_componentsToEnlarge: Component[]) {
        throw new Error("doBeforeComponentEnlarge should not be called on simple components");
    }

    /**
     * This method should be overriden in classes that can contain a component that can be enlarged.  It allows that
     * panel/container to redisplay comoponents that were removed.hidden before other compoents were enlarged.
     *
     * This method is defined here (and not in Container) because of classes like TableRow, which can contain other
     * components, but do not descend from Container.
     */
    public doAfterComponentsShrink() {
        throw new Error("doAfterComponentsShrink should not be called on simple components");
    }

    public get enlarged(): boolean {
        return this._enlarged == null ? false : this._enlarged;
    }

    /**
     * Tell a component to enlarge or shrink.
     * This will also call a method on the defined scope component so it can react accordingly.
     */
    public set enlarged(value: boolean) {
        if (this.enlarged === value)
            return;
        if (this.enlargeScope == null) {
            log.debug(this, "Cannot enlarge component, no scope has been defined");
            return;
        }
        if (value === true)
            this.enlargeScope.doBeforeComponentEnlarge([this]);
        this._enlarged = value;
        this.handleEnlargeOrShrink();
        if (value !== true)
            this.enlargeScope.doAfterComponentsShrink();
    }

    public toggleEnlarged() {
        this.enlarged = !this.enlarged;
    }

    /**
     * This method is intended to be overridden in subclasses to define how that particular component behaves
     * when it is enlarged/shrunk.  This method is needed due to the timing of enlarging/shrinking the component
     * in relation to when the parent panel/container needs to be adjusted.
     */
    handleEnlargeOrShrink() {
    }

    public get enlargeScope(): Component {
        return this._enlargeScope;
    }

    public set enlargeScope(value: Component) {
        this._enlargeScope = value;
    }

    public doWhileDimensionsComputed(callback: (component?: Component) => void) {
        const rect = this._element.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0)
            this.doWhileOffScreen(callback);
        else
            callback(this);
    }

    public doWhileOffScreen(callback: (component?: Component) => void) {
        const prevParent = this._element.parentElement;
        const prevSibling = this._element.nextElementSibling;
        const prevVisible = this.visible;
        const prevPosition = this._element.style.position;
        const prevLeft = this._element.style.left;
        try {
            this.visible = true;
            this._element.style.position = "absolute";
            this._element.style.left = "-9999";
            prevParent?.removeChild(this._element);
            document.body.appendChild(this._element);
            callback(this);
        } finally {
            this.visible = prevVisible;
            this._element.style.position = prevPosition;
            this._element.style.left = prevLeft;
            document.body.removeChild(this._element);
            prevParent?.insertBefore(this._element, prevSibling);
        }
    }

    /**
     * This method is intended to be overridden by sub-classes so that each sub-class can return a list of components
     * that are considered to be standalone components.  The returned list should include the normal 'components' array,
     * as well as components residing in special areas (such as table tools, etc).  It should not return components that
     * are used to make up another component (such as Location's textCombined Textbox).
     * @returns an array of Components that are contained by this component that should be validated by the UI Designer
     */
    public discoverIncludedComponents(): Component[] {
        return null;
    }

    public async ensureLoaded(): Promise<any> {
        await this.loadMetadata();
    }

    public override get selectableInDesigner(): boolean {
        return super.selectableInDesigner === true || this.deserialized === true;
    }

    public get removedByServer(): boolean {
        return this._removedByServer;
    }

    private set removedByServer(value: boolean) {
        this._removedByServer = value;
    }

    public get appProps(): any {
        return this._appProps;
    }

    public set appProps(value: any) {
        this._appProps = value;
    }

    public showContextMenu(event: DomEvent | Event) {
        if (!(event instanceof Event))
            event = new ClickEvent(this, event);
        if (this._designer != null) {
            this._designer.showComponentContextMenu?.(this, event);
        } else {
            new ContextMenu({sourceComponent:this}).showInOverlay(event);
        }
    }

    public get tooltip(): ComponentCreator {
        return this._tooltip;
    }

    public set tooltip(value: ComponentCreator) {
        this._tooltip = value;
        this.tooltipHandler.manageTooltipDisplayEvent();
    }

    public get tooltipCallback(): TooltipCallback {
        return this._tooltipCallback;
    }

    public set tooltipCallback(value: TooltipCallback) {
        this._tooltipCallback = value;
        this.tooltipHandler.manageTooltipDisplayEvent();
    }

    get suppressTooltip(): boolean {
        return this._suppressTooltip;
    }

    set suppressTooltip(value: boolean) {
        this._suppressTooltip = value;
    }

    get tooltipPosition(): Alignment {
        return this.tooltipHandler.tooltipPosition;
    }

    set tooltipPosition(value: Alignment) {
        this.tooltipHandler.tooltipPosition = value;
    }

    public get disabledTooltip(): ComponentCreator {
        return this._disabledTooltip;
    }

    public set disabledTooltip(value: ComponentCreator) {
        this._disabledTooltip = value;
        this.tooltipHandler.manageTooltipDisplayEvent();
    }

    public get tooltipInstance(): Component {
        return this.tooltipHandler.tooltipInstance;
    }

    public set tooltipInstance(value: Component) {
        this.tooltipHandler.tooltipInstance = value;
    }

    public get tooltipAnchor(): HTMLElement {
        return this._element;
    }

    public get isMouseOver() {
        return this.tooltipHandler.isMouseOver;
    }

    showTooltip(text: ComponentCreator, options?: Partial<TooltipOptions>, props?: Partial<PanelProps>) {
        return this.tooltipHandler.showTooltip(text, options, props);
    }

    hideTooltip() {
        this.tooltipHandler.hideTooltip();
    }

    /**
     * This is called by EventListenerList after a DOM event is triggered but before
     * the event listeners for that event are fired.  If gives Component and its subclasses
     * a way to be notified of events so that they can respond as they see fit.
     *
     * This method should return true if the event listeners should be fired, or
     * false if the event listeners should not be fired.
     */
    domEventFiringListeners(event: DomEvent, eventDef: ListenerListDef): boolean {
        const result = this.enabled !== false ||
            eventDef?.activeWhenDisabled === true ||
            (event instanceof DragEvent && this._designer != null);

        if (eventDef === ComponentListenerDefs.click && this.hideTooltipOnClick === true)
            this.hideTooltip();
        return result;
    }

    get hideTooltipOnClick(): boolean {
        return this.tooltipHandler.hideTooltipOnClick;
    }

    set hideTooltipOnClick(value: boolean) {
        this.tooltipHandler.hideTooltipOnClick = value;
    }

    clearTooltipHideTimeout() {
        this.tooltipHandler.clearTooltipHideTimeout();
    }

    public static setPreOverlayMouseOverComponent(comp: Component) {
        ComponentTooltipHandler.setPreOverlayMouseOverComponent(comp);
    }

    public propChangedInDesigner(propName: string, oldValue: any, newValue: any) {
    }

    public addWithAbsolutePosition(component: Component, left: number|string, top: number|string, bottom?: number|string)
    {
        const componentInitiallyVisible = component.visible;
        this._element.style.position = "relative";
        component.visible = false;
        component.style.position = "absolute";
        component.left = left;
        if (top != null)
            component.top = top;
        if (bottom != null)
            component.bottom = bottom;
        this._element.appendChild(component._element);
        if (componentInitiallyVisible === true)
            component.visible = true;
    }

    showAuditLog() {
        AuditLogView.showAudit(this.getParentLayout().layoutName, this.dataSource, this.field);
    }

    protected syncInnerComponentIds() {

    }

    protected setInnerComponentId(innerCompOrElement: Component | HTMLElement, innerId: string) {
        if (innerCompOrElement != null)
            innerCompOrElement.id = this.createInnerComponentId(innerId);
    }

    public createInnerComponentId(suffix: string): string {
        return this.createPrefixedId(suffix);
    }

    public createPrefixedId(suffix: string, delimiter = "-"): string {
        const prefixedId = ComponentUtil.createPrefixedId(this.id, suffix, delimiter);
        if (StringUtil.isEmptyString(prefixedId)) {
            throw new Error("invalid id.");
        }
        return prefixedId;
    }

    public getUnprefixedId(delimiter = "-"): string {
        return StringUtil.stringAfter(this.id, delimiter, false, this.id);
    }

    /**
     * Sub-classes should override this method to control how a props access label is displayed relative to that type of
     * component.  The default is to display the label in the middle of the component.
     */
    displayPropsAccessLabel(label: Label) {
        label.style.position = "absolute";
        label.style.top = "45%";
        label.style.left = "50%";
        label.style.transform = "translate(-50%, -50%)";
        this._element.appendChild(label._element);
    }

    private get componentBehavior(): ComponentBehavior {
        Component._componentBehavior ??= BehaviorTypes.get(Component.name);
        return Component._componentBehavior;
    }
}
